import Box from "@mui/material/Box";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import BootDots from "./BootDots";
import BootShimmer from "./BootShimmer";
import { BOOT_INDICATOR_VISUALS, type BootIndicatorLook, type BootIndicatorVisual } from "./bootIndicatorVisuals";
import { zinc } from "./tokens";
import VerticalLinks, { type VerticalLinkItem } from "./VerticalLinks";

/**
 * The boot look when it maps to `visual`, else `undefined`, so each boot animation fades out when its look ends or
 * the map hands the look to the other animation.
 */
function lookFor(visual: BootIndicatorVisual, bootLook: BootIndicatorLook | undefined) {
  if (!bootLook) return undefined;
  return BOOT_INDICATOR_VISUALS[bootLook] === visual ? bootLook : undefined;
}

/**
 * Full-height icon-rail navigation column: top links, spacer, bottom links.
 *
 * @param bootLook - The boot's look, when it has one; the rail shows the animation `BOOT_INDICATOR_VISUALS` maps it
 *   to. Omitted entirely, the rail reserves no room for the marks.
 */
export default function SidebarNav({
  topLinks,
  bottomLinks,
  bootLook,
}: {
  topLinks: VerticalLinkItem[];
  bottomLinks: VerticalLinkItem[];
  bootLook?: BootIndicatorLook | null;
}) {
  return (
    <Box
      data-sb={TestIds.nav.sidebar}
      sx={{
        width: "100%",
        bgcolor: "rgba(0,0,0,0.5)",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        borderRight: `1px solid ${zinc[700]}`,
        position: "relative",
      }}
    >
      {bootLook !== undefined && <BootShimmer look={lookFor("shimmer", bootLook ?? undefined)} />}
      <Box>
        <VerticalLinks links={topLinks} />
      </Box>
      <Box sx={{ flexGrow: 1, display: "grid", placeItems: "center" }}>
        {bootLook !== undefined ? <BootDots look={lookFor("dots", bootLook ?? undefined)} /> : <>&nbsp;</>}
      </Box>
      <Box>
        <VerticalLinks links={bottomLinks} />
      </Box>
    </Box>
  );
}
